import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ContentOptions } from '../../models/content/content-options.interface';
import { ContentItem } from '../../models/content/content-item.interface';
import { LoginContentResponse } from '../../models/login/login-content-response.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { withCache, requestDataChanged } from '@ngneat/cashew';
import { constants, WINDOW } from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class ContentService {
	private readonly relativeUrl = 'content/';

	constructor(private readonly http: HttpClient, @Inject(WINDOW) protected readonly window: Window) {}

	getLoginContent(contentOptions: ContentOptions): Observable<LoginContentResponse> {
		const client = this.getClientName();

		const httpParamOptions: HttpParamObject =
			HttpUtilityService.toPlainHttpParams(contentOptions);

		return this.http.get<LoginContentResponse>(
			`${this.relativeUrl}general/login/${client}`,
			httpParamOptions
		);
	}

	findByComponent(
		contentType: string,
		component: string,
		contentOptions: ContentOptions
	): Observable<Array<ContentItem>> {
		return this.http.get<Array<ContentItem>>(
			`${this.relativeUrl}${contentType}/components/${component}`,
			{
				params: new HttpParams({ fromObject: { ...contentOptions } }),
				context: withCache({
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
					key: `findByComponent-${contentType}-${component}`,
					clearCachePredicate: requestDataChanged
				})
			}
		);
	}

	findByName(
		contentType: string,
		component: string,
		name: string,
		contentOptions: ContentOptions
	): Observable<Array<ContentItem>> {
		return this.http.get<Array<ContentItem>>(
			`${this.relativeUrl}${contentType}/components/${component}/names/${name}`,
			{
				params: new HttpParams({ fromObject: { ...contentOptions } }),
				context: withCache({
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
					key: `findByName-${contentType}-${component}-${name}`
				})
			}
		);
	}

	getVirtualCardMessage(clientId: string): Observable<Array<ContentItem>> {
		const contentOptions: ContentOptions = {
			program: 'clientDefault',
			textOnly: false,
			client: clientId,
			cascade: false
		};

		return this.http.get<Array<ContentItem>>(
			`${this.relativeUrl}general/components/site/names/virtualCardMessage`,
			{
				params: new HttpParams({ fromObject: { ...contentOptions } }),
				context: withCache({
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
					key: `getVirtualCardMessage-${clientId}`
				})
			}
		);
	}

	getClientName(): string {
		const host = this.window.location.hostname;
		const index = host.indexOf('.');

		if (index < 0) {
			return ''; // sanity check, shouldn't happen in a proper env.
		}

		// Trim client name from front of hostname
		let client = host.slice(0, index);

		// Strip environment postfix from client name if present
		client = client.replace(/-(dev|uat|stg)$/, '');

		return client;
	}
}
